import {
  collection,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment
} from 'firebase/firestore';

import {
  auth,
  db
} from '../utils/firebase';

import {
  createConversationForApplication
} from './messageService';

const APPLICATIONS_COLLECTION =
  'applications';

/*
===============================================================================
APPLY TO JOB
===============================================================================
*/

export async function applyToJob(
  job
) {

  try {

    const candidateId =
      auth.currentUser?.uid;

    if (!candidateId) {

      throw new Error(
        'User must be logged in.'
      );

    }

    const alreadyApplied =
      await hasApplied(
        candidateId,
        job.id
      );

    if (alreadyApplied) {

      return {
        success: false,
        duplicate: true,
        message:
          'You have already applied to this job.'
      };

    }

    const userSnap =
      await getDoc(
        doc(
          db,
          'users',
          candidateId
        )
      );

    if (!userSnap.exists()) {

      throw new Error(
        'Candidate profile not found.'
      );

    }

    const userData =
      userSnap.data();

    const resumeText =
      userData
        ?.masterResume
        ?.resumeText || '';
    
    if (!resumeText.trim()) {

        return {

            success: false,

            error:
            'Please create a resume before applying.'

        };

    }

    const payload = {

      jobId:
        job.id,

      employerId:
        job.employerId,

      candidateId,

      firstName:
        userData.firstName || '',

      lastName:
        userData.lastName || '',

      email:
        userData.email ||
        auth.currentUser.email ||
        '',

      phone:
        userData.phone || '',

      city:
        userData.city || '',

      province:
        userData.province || '',

      resumeText,

      jobTitle:
        job.title,

      company:
        job.company,

      status:
        'Applied',

      notes:
        '',

      rating:
        0,

      appliedAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp()
    };

    const docRef =
      await addDoc(
        collection(
          db,
          APPLICATIONS_COLLECTION
        ),
        payload
      );
      await createConversationForApplication({

        id: docRef.id,

        ...payload

      });
      await updateDoc(
        doc(db, 'jobs', job.id),
        {
            applications:
            increment(1)
        }
        );

    return {

      success: true,

      id:
        docRef.id

    };

  } catch (error) {

    console.error(
      'applyToJob error:',
      error
    );

    return {

      success: false,

      error:
        error.message

    };

  }

}

/*
===============================================================================
CHECK IF ALREADY APPLIED
===============================================================================
*/

export async function hasApplied(
  candidateId,
  jobId
) {

  try {

    const q = query(
      collection(
        db,
        APPLICATIONS_COLLECTION
      ),

      where(
        'candidateId',
        '==',
        candidateId
      ),

      where(
        'jobId',
        '==',
        jobId
      )
    );

    const snapshot =
      await getDocs(q);

    return !snapshot.empty;

  } catch (error) {

    console.error(
      'hasApplied error:',
      error
    );

    return false;

  }

}

/*
===============================================================================
GET EMPLOYER APPLICATIONS
===============================================================================
*/

export async function getEmployerApplications() {

  try {

    const employerId =
      auth.currentUser?.uid;

    if (!employerId) {

      return [];

    }

    const q = query(

      collection(
        db,
        APPLICATIONS_COLLECTION
      ),

      where(
        'employerId',
        '==',
        employerId
      ),

      orderBy(
        'appliedAt',
        'desc'
      )

    );

    const snapshot =
      await getDocs(q);

    return snapshot.docs.map(
      doc => ({

        id:
          doc.id,

        ...doc.data()

      })
    );

  } catch (error) {

    console.error(
      'getEmployerApplications error:',
      error
    );

    return [];

  }

}

/*
===============================================================================
GET JOB APPLICATIONS
===============================================================================
*/

export async function getJobApplications(
  jobId
) {

  try {

    const q = query(

      collection(
        db,
        APPLICATIONS_COLLECTION
      ),

      where(
        'jobId',
        '==',
        jobId
      ),

      orderBy(
        'appliedAt',
        'desc'
      )

    );

    const snapshot =
      await getDocs(q);

    return snapshot.docs.map(
      doc => ({

        id:
          doc.id,

        ...doc.data()

      })
    );

  } catch (error) {

    console.error(
      'getJobApplications error:',
      error
    );

    return [];

  }

}

/*
===============================================================================
UPDATE APPLICATION STATUS
===============================================================================
*/

export async function updateApplicationStatus(
  applicationId,
  status
) {

  try {

    await updateDoc(

      doc(
        db,
        APPLICATIONS_COLLECTION,
        applicationId
      ),

      {

        status,

        updatedAt:
          serverTimestamp()

      }

    );

    return {
      success: true
    };

  } catch (error) {

    console.error(
      'updateApplicationStatus error:',
      error
    );

    return {

      success: false,

      error:
        error.message

    };

  }

}

/*
===============================================================================
SAVE EMPLOYER NOTES
===============================================================================
*/

export async function saveEmployerNotes(
  applicationId,
  notes
) {

  try {

    await updateDoc(

      doc(
        db,
        APPLICATIONS_COLLECTION,
        applicationId
      ),

      {

        notes,

        updatedAt:
          serverTimestamp()

      }

    );

    return {
      success: true
    };

  } catch (error) {

    console.error(
      'saveEmployerNotes error:',
      error
    );

    return {

      success: false,

      error:
        error.message

    };

  }

}

/*
===============================================================================
SAVE EMPLOYER RATING
===============================================================================
*/

export async function saveEmployerRating(
  applicationId,
  rating
) {

  try {

    await updateDoc(

      doc(
        db,
        APPLICATIONS_COLLECTION,
        applicationId
      ),

      {

        rating,

        updatedAt:
          serverTimestamp()

      }

    );

    return {
      success: true
    };

  } catch (error) {

    console.error(
      'saveEmployerRating error:',
      error
    );

    return {

      success: false,

      error:
        error.message

    };

  }

}

export async function markApplicationViewed(
  applicationId
) {

  try {

    await updateDoc(
      doc(
        db,
        APPLICATIONS_COLLECTION,
        applicationId
      ),
      {
        viewed: true,
        viewedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    );

    return {
      success: true
    };

  } catch (error) {

    console.error(
      'markApplicationViewed error:',
      error
    );

    return {
      success: false,
      error: error.message
    };

  }

}
